import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";
import { requireAuth } from "~/lib/auth-guard";
import { Skeleton, useToast } from "@alchema/ui";
import { AiActionButton } from "~/components/AiActionButton";
import { EmptyState } from "~/components/shared/EmptyState";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import type { InterviewQuestion } from "@alchema/shared";
import type { Tier } from "~/lib/tier";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/interview-prep/")({
  beforeLoad: async () => { await requireAuth(); },
  component: PreparePage,
});

const CATEGORY_DOT_COLORS: Record<string, string> = {
  behavioral: "var(--accent)",
  technical: "var(--status-purple, #a78bfa)",
  situational: "var(--warning)",
  "company-specific": "var(--success)",
};

const DIFFICULTY_DOT_COLORS: Record<string, string> = {
  easy: "var(--success)",
  medium: "var(--warning)",
  hard: "var(--error)",
};

function CategoryDot({ category }: { category: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
      <span
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ background: CATEGORY_DOT_COLORS[category] ?? "var(--text-muted)" }}
      />
      <span className="capitalize">{category}</span>
    </span>
  );
}

function DifficultyDot({ difficulty }: { difficulty: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
      <span
        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ background: DIFFICULTY_DOT_COLORS[difficulty] ?? "var(--text-muted)" }}
      />
      <span className="capitalize">{difficulty}</span>
    </span>
  );
}

/**
 * AI-generated interview questions list with EU AI Act Article 50 disclosure.
 * The badge renders adjacent to the questions produced by
 * `orpc.interview.generateForJob`. Renders nothing when there are no
 * questions so the disclosure only appears alongside AI output.
 */
export function GeneratedQuestionsList({
  questions,
}: {
  questions: InterviewQuestion[];
}) {
  if (!questions || questions.length === 0) return null;

  const groupedQuestions = new Map<string, InterviewQuestion[]>();
  for (const q of questions) {
    const existing = groupedQuestions.get(q.category) ?? [];
    existing.push(q);
    groupedQuestions.set(q.category, existing);
  }

  return (
    <>
      {/* AI transparency badge (EU AI Act Article 50) */}
      <div className="mb-4">
        <AiGeneratedBadge />
      </div>

      <div className="space-y-5">
        {Array.from(groupedQuestions.entries()).map(([category, categoryQuestions]) => (
          <div key={category}>
            <div className="mb-2">
              <CategoryDot category={category} />
            </div>
            <div className="space-y-1">
              {categoryQuestions.map((q, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 rounded-lg px-3 py-2.5"
                  style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)" }}
                >
                  <DifficultyDot difficulty={q.difficulty} />
                  <p className="text-sm flex-1" style={{ color: "var(--text)" }}>{q.question}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function PreparePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedJobId, setSelectedJobId] = React.useState<string>("");
  const [activeSessionId, setActiveSessionId] = React.useState<string | null>(null);
  const [briefExpanded, setBriefExpanded] = React.useState(false);

  const creditQuery = useQuery(orpc.credits.status.queryOptions({ input: undefined }));
  const credits = creditQuery.data;

  const jobsQuery = useQuery(orpc.job.list.queryOptions());
  const sessionsQuery = useQuery(orpc.interview.listSessions.queryOptions());

  const generateMutation = useMutation(
    orpc.interview.generateForJob.mutationOptions({
      onSuccess: (data) => {
        setActiveSessionId(data.id);
        queryClient.invalidateQueries({ queryKey: orpc.interview.listSessions.key({}) });
        toast(m.interview_prepare_toast_generated(), "success");
      },
      onError: (err) => toast(err.message || m.interview_prepare_toast_generate_error(), "error"),
    }),
  );

  const briefMutation = useMutation(
    orpc.interview.getCompanyBrief.mutationOptions({
      onSuccess: () => {
        if (activeSessionId) {
          queryClient.invalidateQueries({
            queryKey: orpc.interview.getSession.key({ input: { sessionId: activeSessionId } }),
          });
        }
        toast(m.interview_prepare_toast_brief_generated(), "success");
      },
      onError: (err) => toast(err.message || m.interview_prepare_toast_brief_error(), "error"),
    }),
  );

  const sessionQuery = useQuery({
    ...orpc.interview.getSession.queryOptions({ input: { sessionId: activeSessionId! } }),
    enabled: !!activeSessionId,
  });

  // Sort jobs: interview status first, then applied
  const sortedJobs = React.useMemo(() => {
    if (!jobsQuery.data) return [];
    return [...jobsQuery.data].sort((a, b) => {
      const priority: Record<string, number> = { interview: 0, applied: 1, saved: 2, offer: 3, rejected: 4 };
      return (priority[a.status] ?? 5) - (priority[b.status] ?? 5);
    });
  }, [jobsQuery.data]);

  const activeSession = sessionQuery.data;

  if (jobsQuery.isLoading || sessionsQuery.isLoading) {
    return (
      <div className="space-y-8" aria-busy="true" role="status" aria-label="Loading interview prep">
        <div
          className="rounded-xl p-6"
          style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
        >
          <Skeleton className="h-6 w-48" />
          <Skeleton className="mt-2 h-4 w-64" />
          <div className="mt-4 flex items-end gap-3">
            <Skeleton className="h-10 flex-1 rounded-lg" />
            <Skeleton className="h-10 w-32 rounded-lg" />
          </div>
        </div>
        <div>
          <Skeleton className="h-6 w-40 mb-3" />
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (jobsQuery.isError || sessionsQuery.isError) {
    return (
      <div
        className="rounded-xl p-8 text-center"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {m.interview_error_load()}
        </p>
        <button onClick={() => { jobsQuery.refetch(); sessionsQuery.refetch(); }} className="btn btn-ghost btn-sm mt-3">
          {m.interview_error_retry()}
        </button>
      </div>
    );
  }

  if (sortedJobs.length === 0) {
    return (
      <EmptyState
        icon={
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
        }
        title={m.interview_empty_title()}
        description={m.interview_empty_desc()}
        action={{ label: m.interview_empty_cta(), href: "/jobs" }}
      />
    );
  }

  // Session stats for whisper stat blocks
  const totalAnswered = sessionsQuery.data?.reduce((sum, s) => sum + s.answerCount, 0) ?? 0;
  const totalSessions = sessionsQuery.data?.length ?? 0;

  return (
    <div className="space-y-8">

      {/* Stat blocks row */}
      {totalSessions > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 animate-fade-up">
          {[
            { label: "Sessions", value: totalSessions },
            { label: "Answered", value: totalAnswered },
            {
              label: "Topics",
              value: new Set(
                sessionsQuery.data?.flatMap((s) =>
                  s.questions.map((q: InterviewQuestion) => q.category),
                ) ?? [],
              ).size,
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl px-4 py-3"
              style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
            >
              <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                {stat.label}
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums" style={{ color: "var(--text)" }}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Job Selector and Generate */}
      <div
        className="rounded-xl p-6"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>{m.interview_prepare_title()}</h2>
        <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
          {m.interview_prepare_subtitle()}
        </p>
        <div className="mt-4 flex items-end gap-3">
          <div className="flex-1">
            <label htmlFor="ip-job-select" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
              {m.interview_prepare_label_job()}
            </label>
            <select
              id="ip-job-select"
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1"
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                outlineColor: "var(--accent)",
              }}
            >
              <option value="">{m.interview_prepare_select_job()}</option>
              {sortedJobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {m.interview_prepare_job_option({ title: job.title, company: job.company, status: job.status })}
                </option>
              ))}
            </select>
          </div>
          <AiActionButton
            creditCost={1}
            onClick={() => generateMutation.mutate({ jobId: selectedJobId })}
            disabled={!selectedJobId || generateMutation.isPending}
            creditsRemaining={credits?.totalAvailable ?? credits?.balance ?? 0}
            creditLimit={credits?.limit ?? 0}
            creditResetAt={credits?.resetAt ? new Date(credits.resetAt) : new Date()}
            tier={(credits?.tier as Tier) ?? "free"}
            className="btn btn-primary"
          >
            {generateMutation.isPending ? (
              <span className="flex items-center gap-2">
                <svg aria-hidden="true" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {m.interview_prepare_generating()}
              </span>
            ) : (
              m.interview_prepare_generate_cta()
            )}
          </AiActionButton>
        </div>
      </div>

      {/* Active Session */}
      {activeSession && (
        <div className="space-y-4">
          {/* Questions grouped by category */}
          <div
            className="rounded-xl p-6"
            style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                {m.interview_prepare_questions_count({ count: activeSession.questions.length })}
              </h2>
              <Link
                to="/interview-prep/practice"
                search={{ sessionId: activeSession.id }}
                className="btn btn-primary btn-sm"
              >
                {m.interview_prepare_start_practice()}
              </Link>
            </div>

            <GeneratedQuestionsList questions={activeSession.questions} />
          </div>

          {/* Company Brief */}
          <div
            className="rounded-xl p-5"
            style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
          >
            <button
              onClick={() => {
                if (!activeSession.companyBrief) {
                  briefMutation.mutate({ sessionId: activeSession.id });
                }
                setBriefExpanded(!briefExpanded);
              }}
              className="flex w-full items-center justify-between"
              aria-expanded={briefExpanded}
              aria-controls="company-brief-panel"
            >
              <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>{m.interview_prepare_company_brief()}</h2>
              <svg
                aria-hidden="true"
                className={`h-4 w-4 transition ${briefExpanded ? "rotate-180" : ""}`}
                style={{ color: "var(--text-muted)" }}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
            {briefExpanded && (
              <div id="company-brief-panel" role="region" aria-label={m.interview_prepare_company_brief()} className="mt-4">
                {briefMutation.isPending ? (
                  <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
                    <svg aria-hidden="true" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {m.interview_prepare_generating_brief()}
                  </div>
                ) : activeSession.companyBrief ? (
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm" style={{ color: "var(--text-secondary)" }}>
                    {activeSession.companyBrief}
                  </div>
                ) : (
                  <AiActionButton
                    creditCost={1}
                    onClick={() => briefMutation.mutate({ sessionId: activeSession.id })}
                    creditsRemaining={credits?.totalAvailable ?? credits?.balance ?? 0}
                    creditLimit={credits?.limit ?? 0}
                    creditResetAt={credits?.resetAt ? new Date(credits.resetAt) : new Date()}
                    tier={(credits?.tier as Tier) ?? "free"}
                    className="btn btn-ghost btn-sm"
                  >
                    {m.interview_prepare_generate_brief()}
                  </AiActionButton>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Sessions */}
      {sessionsQuery.data && sessionsQuery.data.length > 0 && (
        <div>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
            {m.interview_prepare_previous_sessions()}
          </h2>
          <div className="space-y-1">
            {sessionsQuery.data.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between rounded-xl px-4 py-3 transition-colors"
                style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>
                    {session.jobTitle ?? m.interview_prepare_general()}
                    {session.jobCompany ? (
                      <span className="font-normal" style={{ color: "var(--text-muted)" }}>
                        {" "}{m.interview_prepare_at_company({ company: session.jobCompany })}
                      </span>
                    ) : null}
                  </p>
                  <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>
                    {m.interview_prepare_session_stats({ questions: session.questions.length, answered: session.answerCount })}
                    {" · "}
                    {new Date(session.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2 ml-4 shrink-0">
                  <button
                    onClick={() => setActiveSessionId(session.id)}
                    className="btn btn-ghost btn-sm"
                  >
                    {m.interview_prepare_view()}
                  </button>
                  <Link
                    to="/interview-prep/practice"
                    search={{ sessionId: session.id }}
                    className="btn btn-primary btn-sm"
                  >
                    {session.answerCount > 0 ? m.interview_prepare_resume_practice() : m.interview_prepare_start_practice()}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
